// frontend/src/pages/CandidateReport.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { showSuccess, showError, showInfo } from '../utils/toastUtils';
import api from '../services/api';
import {
    ArrowLeftIcon,
    DocumentReportIcon,
    XCircleIcon,
    DownloadIcon
} from '@heroicons/react/outline';
import { getCandidate } from '../services/adminApi';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ============================================
// LABEL LOOKUP MAPS (fallback only)
// Real factor list comes from reportData.factorScores
// ============================================
const FACTOR_LABELS = {
    'ESST-1': 'Emotional Stability & Stress Tolerance',
    'IEB-2': 'Integrity & Ethical Behaviour',
    'RCD-3': 'Rule Compliance & Work Discipline',
    'WCR-4': 'Work Commitment & Reliability',
    'SBAC-5': 'Social Behaviour & Aggression Control',
    'AMLC-6': 'Adaptability to Migrant Living Conditions',
    'IFTLA-7': 'Instruction Following & Task Learning Ability',
};

const FACET_LABELS = {
    'ESST-ER-01': 'Emotional Regulation',
    'ESST-ST-02': 'Stress Tolerance',
    'ESST-LC-03': 'Loneliness Coping',
    'ESST-FT-04': 'Frustration Tolerance',

    'IEB-HT-01': 'Honesty',
    'IEB-RT-02': 'Responsibility',
    'IEB-MD-03': 'Moral Decision Making',
    'IEBB-FD-04': 'Financial Discipline',

    'RCD-AA-01': 'Authority Acceptance',
    'RCD-RA-02': 'Rule Adherence',
    'RCD-SO-03': 'Safety Orientation',
    'RCD-TD-04': 'Task Discipline',

    'WCR-WP-01': 'Work Persistence',
    'WCR-DT-02': 'Dependability',
    'WCR-RE-03': 'Responsibility Toward Employer',
    'WCR-MW-04': 'Motivation for Overseas Work',

    'SBAC-AM-01': 'Anger Management',
    'SBAC-RI-02': 'Respectful Interaction',
    'SBAC-CA-03': 'Conflict Avoidance',
    'SBAC-TC-04': 'Team Cooperation',

    'AMLC-CA-01': 'Cultural Adaptability',
    'AMLC-ST-02': 'Shared Living Tolerance',
    'AMLC-RA-03': 'Routine Adaptability',
    'AMLC-EA-04': 'Environmental Adjustment',

    'IFTLA-IC-01': 'Instruction Comprehension',
    'IFTLA-AD-02': 'Attention to Detail',
    'IFTLA-LR-03': 'Learning Readiness',
    'IFTLA-OA-04': 'Observation Accuracy',
};

// ============================================
// HELPERS
// ============================================
const isHigh = (interp) => {
    if (!interp) return false;
    const g = (interp.grade || '').toString().toLowerCase();
    return g === 'high' || g === 'very good' || g === 'excellent' || g === 'good';
};

const isLow = (interp) => {
    if (!interp) return false;
    const g = (interp.grade || '').toString().toLowerCase();
    return g === 'low' || g === 'very low' || g === 'below average';
};

const isAverage = (interp) => {
    if (!interp) return false;
    const g = (interp.grade || '').toString().toLowerCase();
    return g === 'average' || g === 'medium';
};

const getStanineFromTScore = (tScore) => {
    if (tScore < 32) return 1;
    if (tScore < 37) return 2;
    if (tScore < 42) return 3;
    if (tScore < 47) return 4;
    if (tScore < 53) return 5;
    if (tScore < 58) return 6;
    if (tScore < 63) return 7;
    if (tScore < 68) return 8;
    return 9;
};

const getGradeLabelFromStanine = (stanine) => {
    if (stanine <= 4) return 'Low';
    if (stanine <= 7) return 'Average';
    return 'High';
};

const getSelectionIndication = (stanine) => {
    if (stanine <= 4) return { label: 'Not Suitable', bg: 'bg-red-500', text: 'text-white' };
    if (stanine <= 7) return { label: 'Monitor / Support', bg: 'bg-yellow-500', text: 'text-white' };
    return { label: 'Suitable', bg: 'bg-green-600', text: 'text-white' };
};

const getStanineBarColor = (stanine) => {
    if (stanine <= 4) return 'bg-red-500';
    if (stanine <= 7) return 'bg-yellow-500';
    return 'bg-green-600';
};

const getGradeBadgeBg = (grade) => {
    const g = (grade || '').toString().toLowerCase();
    if (g === 'low') return 'bg-red-100 text-red-800';
    if (g === 'average' || g === 'medium') return 'bg-yellow-100 text-yellow-800';
    if (g === 'high') return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
};

const getGradeTextColor = (grade) => {
    const g = (grade || '').toString().toLowerCase();
    if (g === 'low') return 'text-red-700';
    if (g === 'average' || g === 'medium') return 'text-yellow-700';
    if (g === 'high') return 'text-green-700';
    return 'text-gray-700';
};

// ============================================
// COMPONENT
// ============================================
const CandidateReport = () => {
    const { candidateId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [candidate, setCandidate] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const reportRef = useRef(null);

    useEffect(() => {
        if (candidateId) fetchCandidateAndReport();
    }, [candidateId]);

    const fetchCandidateAndReport = async () => {
        try {
            setLoading(true);
            setError(null);

            const candidateResponse = await getCandidate(candidateId);

            if (candidateResponse.data.success) {
                const candidateData = candidateResponse.data.candidate;
                setCandidate(candidateData);

                if (candidateData.status === 'completed' && candidateData.access_token) {
                    try {
                        const reportResponse = await api.get(`/reports/${candidateData.access_token}`);
                        if (reportResponse.data.success) {
                            console.log('📊 Report loaded:', reportResponse.data.report);
                            setReportData(reportResponse.data.report);
                        }
                    } catch (reportError) {
                        console.log('⚠️ Error fetching report:', reportError.message);
                    }
                }
            }
        } catch (error) {
            console.error('❌ Error:', error);
            setError('Failed to load candidate report');
            showError('Failed to load candidate report');
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // BUILD DATA STRUCTURES
    // ============================================
    const buildFactorData = () => {
        if (!reportData) return [];

        // ─────────────────────────────────────────
        // Locate factor scores across possible keys
        // ─────────────────────────────────────────
        const factorScores =
            reportData.factorScores ||
            reportData.factorTScores ||
            reportData.scales ||
            {};

        // ─────────────────────────────────────────
        // Locate facet scores across ALL possible keys.
        // Merge them so any source that has them is picked up.
        // ─────────────────────────────────────────
        const facetScores = {
            ...(reportData.facetScores || {}),
            ...(reportData.facetsScores || {}),
            ...(reportData.facetTScores || {}),
            ...(reportData.facets || {}),
        };

        // Also: if a merged `scales` map contains both factor and facet IDs,
        // peel out the facet-looking keys (those with a hyphen and non-`-1`/`-2` suffix)
        // and merge them into facetScores.
        const mergedScales = reportData.scales || {};
        for (const [id, val] of Object.entries(mergedScales)) {
            // Facet IDs typically look like PREFIX-XX-NN (3 segments) vs factor IDs (2 segments)
            const parts = id.split('-');
            if (parts.length >= 3 && !facetScores[id]) {
                facetScores[id] = val;
            }
        }

        // Also: if a factor score object has its own `facets` sub-object,
        // hoist those into the flat facetScores map (if not already there).
        for (const [factorId, fScore] of Object.entries(factorScores)) {
            const nested = fScore?.facets || fScore?.facetScores;
            if (nested && typeof nested === 'object') {
                for (const [facetId, v] of Object.entries(nested)) {
                    if (!facetScores[facetId]) facetScores[facetId] = v;
                }
            }
        }

        const groupedInterps = reportData.groupedInterpretations || {};
        const allInterps = reportData.interpretations || {};

        console.log('🏗️ [buildFactorData] factor IDs:', Object.keys(factorScores));
        console.log('🏗️ [buildFactorData] facet IDs:', Object.keys(facetScores));

        const factors = [];
        let index = 1;

        for (const [factorId, score] of Object.entries(factorScores)) {
            if (!score) continue;
            if (factorId.startsWith('_')) continue;

            // Skip items that look like facets (3+ segments in ID)
            // so we don't render a facet as if it were a factor.
            const idSegments = factorId.split('-').length;
            // Our factor IDs are like ESST-1 (2 segments). Facets are ESST-ER-01 (3).
            // Only skip if we're sure it's a facet AND we already have factors.
            // To be safe, keep it — the report renders whichever IDs are in factorScores.
            // (If your payload mixes them, adjust this filter.)

            const label = reportData.factorNames[factorId]; //FACTOR_LABELS[factorId] || score.factorName || score.factor_name || factorId;

            const stanine = score.stanine
                ?? getStanineFromTScore(score.tScore ?? score.t_score ?? 50);
            const tScore = score.tScore ?? score.t_score ?? 50;
            const grade = getGradeLabelFromStanine(stanine);
            const indication = getSelectionIndication(stanine);

            const rawInterp =
                groupedInterps[factorId]?.factorInterpretation ||
                allInterps[factorId] ||
                null;

            const factorInterp = rawInterp ? { ...rawInterp, grade } : null;

            // ─────────────────────────────────────────
            // Facets for this factor
            // ─────────────────────────────────────────
            const facets = [];

            for (const [facetId, facetScore] of Object.entries(facetScores)) {
                if (!facetScore) continue;

                // Match if ANY of the following:
                //  1. Facet ID starts with the factor ID (`ESST-1` → `ESST-1-01`)
                //  2. Grouped interps tie this facet to the factor
                //  3. Facet ID shares a family prefix with the factor
                //     (e.g. `ESST-1` and `ESST-ER-01` share `ESST`)
                const sharesFullPrefix =
                    facetId === factorId ||
                    facetId.startsWith(`${factorId}-`) ||
                    facetId.startsWith(`${factorId}_`);

                const sharesFamilyPrefix = (() => {
                    // Factor `ESST-1` → family `ESST`
                    // Facet `ESST-ER-01` → family `ESST`
                    const factorFamily = factorId.split('-')[0];
                    const facetFamily = facetId.split('-')[0];
                    return factorFamily && facetFamily && factorFamily === facetFamily;
                })();

                const inGroup = groupedInterps[factorId]?.facets?.some(f => f.facetId === facetId);

                // Skip if it doesn't belong to this factor
                if (!sharesFullPrefix && !sharesFamilyPrefix && !inGroup) continue;

                const facetStanine = facetScore.stanine
                    ?? getStanineFromTScore(facetScore.tScore ?? facetScore.t_score ?? tScore);

                const rawFacetInterp =
                    groupedInterps[factorId]?.facets?.find(f => f.facetId === facetId)?.facetInterpretation ||
                    allInterps[facetId] ||
                    null;

                facets.push({
                    facetId,
                    label: reportData.facetNames[facetId], //FACET_LABELS[facetId] || facetScore.facetName || facetScore.facet_name || facetId,
                    stanine: facetStanine,
                    rawScore: facetScore.rawScore ?? 0,
                    tScore: facetScore.tScore ?? facetScore.t_score ?? tScore,
                    grade: getGradeLabelFromStanine(facetStanine),
                    interpretation: rawFacetInterp
                        ? { ...rawFacetInterp, grade: getGradeLabelFromStanine(facetStanine) }
                        : null,
                });
            }

            factors.push({
                no: index++,
                factorId,
                label,
                rawScore: score.rawScore ?? 0,
                tScore,
                stanine,
                grade,
                indication,
                interpretation: factorInterp,
                facets,
                hasFacets: facets.length > 0,
            });
        }

        factors.sort((a, b) => {
            const numA = parseInt((a.factorId.match(/(\d+)/) || [0, 0])[1], 10);
            const numB = parseInt((b.factorId.match(/(\d+)/) || [0, 0])[1], 10);
            return numA - numB;
        });
        factors.forEach((f, i) => { f.no = i + 1; });

        console.log('🏗️ [buildFactorData] built factors:', factors.map(f => ({
            id: f.factorId,
            label: f.label,
            stanine: f.stanine,
            facets: f.facets.length,
            facetIds: f.facets.map(x => x.facetId),
        })));

        return factors;
    };

    // ============================================
    // RENDER STANINE BAR
    // ============================================
    const renderStanineBar = (stanine, width = 'full') => {
        return (
            <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <div
                        key={num}
                        className={`h-4 ${width === 'full' ? 'w-5' : 'w-3.5'} rounded-sm ${num <= stanine ? getStanineBarColor(stanine) : 'bg-gray-100'
                            }`}
                        title={`Stanine ${num}`}
                    />
                ))}
            </div>
        );
    };

    // ============================================
    // HEADER
    // ============================================
    const renderHeader = () => (
        <div className="flex items-start justify-between border-b-2 border-gray-200 pb-4 mb-6">
            <div className="text-center flex-1">
                <h1 className="text-lg font-bold text-[#063b56] uppercase tracking-wide">
                    Psychological Suitability Assessment
                </h1>
            </div>
        </div>
    );

    // ============================================
    // SECTION 1: CANDIDATE INFORMATION
    // ============================================
    const renderCandidateInfo = () => {
        const grade = reportData?.grade || reportData?.overallGrade || 'Average';
        const totalStanine = reportData?.totalStanine
            ?? (reportData?.totalTScore
                ? getStanineFromTScore(reportData.totalTScore)
                : 6);

        const overallReco =
            grade === 'High' || totalStanine >= 8 ? 'RECOMMENDED FOR SELECTION' :
                grade === 'Average' || totalStanine >= 5 ? 'RECOMMENDED WITH SUPPORT' :
                    'NOT RECOMMENDED';

        const overallColor =
            overallReco === 'RECOMMENDED FOR SELECTION' ? 'bg-green-600' :
                overallReco === 'RECOMMENDED WITH SUPPORT' ? 'bg-amber-500' :
                    'bg-red-600';

        const proxiedPhotoUrl = candidate?.id
            ? `${API_BASE}/face-photos/proxy/${candidate.id}/1`
            : null;

        const facePhotoUrl = proxiedPhotoUrl ||
            reportData?.facePhotoUrl ||
            reportData?.facePhoto1Url ||
            reportData?.candidate?.facePhotoUrl ||
            null;

        const fields = [];

        const name = candidate?.full_name || candidate?.candidate_name;
        if (name) fields.push({ label: 'Name', value: name });

        if (candidate?.id) {
            fields.push({ label: 'Candidate ID', value: `CND${String(candidate.id).padStart(6, '0')}` });
        }

        const phone = candidate?.phone || candidate?.candidate_phone;
        if (phone) fields.push({ label: 'Phone', value: phone });

        if (candidate?.aadhar_number) {
            fields.push({
                label: 'Aadhar Number',
                value: candidate.aadhar_number.replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4')
            });
        }

        if (candidate?.date_of_birth) {
            fields.push({
                label: 'Date of Birth',
                value: new Date(candidate.date_of_birth).toLocaleDateString('en-GB', {
                    day: '2-digit', month: 'short', year: 'numeric'
                })
            });
        }

        if (candidate?.age) fields.push({ label: 'Age', value: `${candidate.age} years` });

        const sex = candidate?.sex || candidate?.gender;
        if (sex) fields.push({ label: 'Gender', value: sex });

        if (candidate?.address) fields.push({ label: 'Address', value: candidate.address });

        if (candidate?.department_name) {
            fields.push({ label: 'Department', value: candidate.department_name });
        }

        if (candidate?.assessment_id) {
            fields.push({ label: 'Assessment ID', value: candidate.assessment_id });
        }

        const completedDate = candidate?.completed_at || candidate?.started_at;
        if (completedDate) {
            fields.push({
                label: 'Assessment Date',
                value: new Date(completedDate).toLocaleDateString('en-GB', {
                    day: '2-digit', month: 'short', year: 'numeric'
                })
            });
        }

        return (
            <section className="mb-6">
                <div className="bg-[#063b56] text-white px-4 py-2 rounded-t-lg flex items-center gap-2">
                    <span className="bg-white text-[#063b56] w-6 h-6 rounded-full flex items-center justify-center font-bold text-sm">1</span>
                    <h2 className="font-bold text-sm tracking-wide">CANDIDATE INFORMATION</h2>
                </div>

                <div className="border border-gray-200 rounded-b-lg p-4">
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-2">
                            <div className="aspect-[3/4] bg-gray-100 rounded border border-gray-300 overflow-hidden">
                                {facePhotoUrl ? (
                                    <img
                                        src={facePhotoUrl}
                                        alt={name || 'Candidate'}
                                        data-photo-candidate="true"
                                        crossOrigin="anonymous"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = `
                                            <div class="w-full h-full flex items-center justify-center bg-gray-100">
                                                <svg class="w-16 h-16 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                                </svg>
                                            </div>
                                        `;
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                        <svg className="w-16 h-16 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="col-span-6">
                            <div className="space-y-2 text-sm">
                                {fields.length > 0 ? (
                                    fields.map((field, idx) => (
                                        <div key={idx} className="grid grid-cols-3 gap-2">
                                            <span className="text-gray-500">{field.label}</span>
                                            <span className="col-span-2">: {field.value}</span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-gray-400 italic text-sm">No candidate information available</p>
                                )}
                            </div>
                        </div>

                        <div className="col-span-4">
                            <div className="border-2 border-green-200 rounded-lg overflow-hidden">
                                <div className="bg-green-50 py-2 text-center text-xs font-bold text-green-800 tracking-wide">
                                    OVERALL RECOMMENDATION
                                </div>
                                <div className={`${overallColor} text-white py-4 px-3 flex flex-col items-center`}>
                                    <svg className="w-10 h-10 mb-2" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <div className="text-center font-bold text-lg leading-tight">
                                        {overallReco}
                                    </div>
                                </div>
                                <div className="bg-green-50 p-2 text-xs text-green-900">
                                    The candidate demonstrates an overall profile consistent with the psychological and behavioural demands of the role.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    };

    // ============================================
    // SECTION 2: ASSESSMENT RESULTS SUMMARY
    // ============================================
    const renderResultsSummary = () => {
        const factors = buildFactorData();

        return (
            <section className="mb-6">
                <div className="bg-[#063b56] text-white px-4 py-2 rounded-t-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="bg-white text-[#063b56] w-6 h-6 rounded-full flex items-center justify-center font-bold text-sm">2</span>
                        <h2 className="font-bold text-sm tracking-wide">ASSESSMENT RESULTS SUMMARY</h2>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                        <span className="text-gray-300">Score Guide</span>
                        <span className="bg-red-500 px-2 py-1 rounded">1-4 Low</span>
                        <span className="bg-yellow-500 px-2 py-1 rounded text-black">5-7 Average</span>
                        <span className="bg-green-600 px-2 py-1 rounded">8-10 High</span>
                    </div>
                </div>

                <div className="border border-gray-200 rounded-b-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-blue-50">
                            <tr className="text-xs text-gray-700">
                                <th className="px-3 py-2 text-left w-12">No.</th>
                                <th className="px-3 py-2 text-left">Factor</th>
                                <th className="px-3 py-2 text-center w-24">Stanine Score</th>
                                <th className="px-3 py-2 text-left w-64">1  2  3  4  5  6  7  8  9  10</th>
                                <th className="px-3 py-2 text-center w-28">Factor Grade</th>
                                <th className="px-3 py-2 text-center w-32">Selection Indication</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {factors.map((factor) => (
                                <tr key={factor.factorId}>
                                    <td className="px-3 py-2 text-center font-medium">{factor.no}</td>
                                    <td className="px-3 py-2">{factor.label}</td>
                                    <td className="px-3 py-2 text-center font-bold text-lg">{factor.stanine}</td>
                                    <td className="px-3 py-2">{renderStanineBar(factor.stanine)}</td>
                                    <td className="px-3 py-2 text-center">
                                        <span className={`inline-block px-3 py-1 rounded font-medium text-xs ${getGradeBadgeBg(factor.grade)}`}>
                                            {factor.grade}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <span className={`inline-block px-3 py-1 rounded font-bold text-xs ${factor.indication.bg} ${factor.indication.text}`}>
                                            {factor.indication.label}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            <tr className="bg-gray-50 font-bold">
                                <td className="px-3 py-2"></td>
                                <td className="px-3 py-2">Overall Profile</td>
                                <td className="px-3 py-2 text-center text-lg">
                                    {reportData?.totalStanine ??
                                        (reportData?.totalTScore
                                            ? getStanineFromTScore(reportData.totalTScore)
                                            : Math.round(factors.reduce((s, f) => s + f.stanine, 0) / (factors.length || 1)))}
                                </td>
                                <td className="px-3 py-2">
                                    {renderStanineBar(
                                        reportData?.totalStanine ??
                                        (reportData?.totalTScore
                                            ? getStanineFromTScore(reportData.totalTScore)
                                            : 6)
                                    )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                    <span className={`inline-block px-3 py-1 rounded text-xs ${getGradeBadgeBg(reportData?.overallGrade || 'Average')}`}>
                                        {reportData?.overallGrade || 'N/A'}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-center">
                                    <span className="inline-block px-3 py-1 bg-green-700 text-white rounded text-xs">
                                        {reportData?.recommendation?.toUpperCase().includes('NOT')
                                            ? 'NOT RECOMMENDED'
                                            : reportData?.recommendation?.toUpperCase().includes('SUPPORT')
                                                ? 'RECOMMENDED WITH SUPPORT'
                                                : 'RECOMMENDED'}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        );
    };

    // ============================================
    // SECTION 3: DETAILED INTERPRETATION
    // ============================================
    const renderDetailedInterpretation = () => {
        const factors = buildFactorData();

        return (
            <section className="mb-6">
                <div className="bg-[#063b56] text-white px-4 py-2 rounded-t-lg flex items-center gap-2">
                    <span className="bg-white text-[#063b56] w-6 h-6 rounded-full flex items-center justify-center font-bold text-sm">3</span>
                    <h2 className="font-bold text-sm tracking-wide">DETAILED INTERPRETATION</h2>
                </div>

                <div className="border border-gray-200 rounded-b-lg p-4 space-y-4">
                    {factors.map((factor) => {
                        const factorInterp = factor.interpretation;

                        return (
                            <div key={factor.factorId} className="border border-gray-200 rounded-lg overflow-hidden avoid-break">
                                {/* Factor header row */}
                                <div className="bg-blue-50 px-3 py-2 flex items-center justify-between border-b border-gray-200">
                                    <div className="flex items-center gap-2">
                                        <span className="bg-[#063b56] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                                            {factor.no}
                                        </span>
                                        <span className="font-bold text-sm text-[#063b56]">{factor.label}</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs">
                                        <span>Factor Stanine: <span className="font-bold">{factor.stanine}</span></span>
                                        <span className="text-gray-300">|</span>
                                        <span>Grade: <span className={`font-bold ${getGradeTextColor(factor.grade)}`}>{factor.grade}</span></span>
                                        <span className="text-gray-300">|</span>
                                        <span>Indication: <span className="font-bold text-green-700">{factor.indication.label}</span></span>
                                    </div>
                                </div>

                                {/* ============================================ */}
                                {/* ⭐ FACET-LESS → compact single-column */}
                                {/* ============================================ */}
                                {!factor.hasFacets ? (
                                    <div className="p-4">
                                        <p className="text-xs text-gray-700 leading-relaxed">
                                            {factorInterp?.interpretationText ||
                                                factorInterp?.interpretation_text ||
                                                factorInterp?.low_score_interpretation ||
                                                factorInterp?.average_score_interpretation ||
                                                factorInterp?.high_score_interpretation || (
                                                    <span className="italic text-gray-400">No interpretation available</span>
                                                )}
                                        </p>

                                        {isHigh(factorInterp) && factorInterp?.strengths && (
                                            <div className="mt-3 p-2 bg-green-50 border-l-4 border-green-500 rounded text-xs">
                                                <p className="font-bold text-green-700 mb-1">✓ Strengths</p>
                                                <p className="text-green-800 whitespace-pre-line">{factorInterp.strengths}</p>
                                            </div>
                                        )}

                                        {isLow(factorInterp) && (
                                            <>
                                                {factorInterp?.developmentAreas && (
                                                    <div className="mt-3 p-2 bg-orange-50 border-l-4 border-orange-500 rounded text-xs">
                                                        <p className="font-bold text-orange-700 mb-1">⚡ Development Areas</p>
                                                        <p className="text-orange-800 whitespace-pre-line">{factorInterp.developmentAreas}</p>
                                                    </div>
                                                )}
                                                {factorInterp?.recommendations && (
                                                    <div className="mt-3 p-2 bg-blue-50 border-l-4 border-blue-500 rounded text-xs">
                                                        <p className="font-bold text-blue-700 mb-1">💡 Recommendations</p>
                                                        <p className="text-blue-800 whitespace-pre-line">{factorInterp.recommendations}</p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    /* ============================================ */
                                    /* ⭐ WITH FACETS → original two-column layout, unchanged */
                                    /* ============================================ */
                                    <div className="grid grid-cols-12 gap-3 p-3">
                                        {/* Left: Factor interpretation text */}
                                        <div className="col-span-4">
                                            <p className="text-xs text-gray-700 leading-relaxed">
                                                {factorInterp?.interpretationText ||
                                                    factorInterp?.interpretation_text ||
                                                    factorInterp?.low_score_interpretation ||
                                                    factorInterp?.average_score_interpretation ||
                                                    factorInterp?.high_score_interpretation || (
                                                        <span className="italic text-gray-400">No interpretation available</span>
                                                    )}
                                            </p>

                                            {isHigh(factorInterp) && factorInterp?.strengths && (
                                                <div className="mt-2 p-2 bg-green-50 border-l-4 border-green-500 rounded text-xs">
                                                    <p className="font-bold text-green-700 mb-1">✓ Strengths</p>
                                                    <p className="text-green-800 whitespace-pre-line">{factorInterp.strengths}</p>
                                                </div>
                                            )}

                                            {isLow(factorInterp) && (
                                                <>
                                                    {factorInterp?.developmentAreas && (
                                                        <div className="mt-2 p-2 bg-orange-50 border-l-4 border-orange-500 rounded text-xs">
                                                            <p className="font-bold text-orange-700 mb-1">⚡ Development Areas</p>
                                                            <p className="text-orange-800 whitespace-pre-line">{factorInterp.developmentAreas}</p>
                                                        </div>
                                                    )}
                                                    {factorInterp?.recommendations && (
                                                        <div className="mt-2 p-2 bg-blue-50 border-l-4 border-blue-500 rounded text-xs">
                                                            <p className="font-bold text-blue-700 mb-1">💡 Recommendations</p>
                                                            <p className="text-blue-800 whitespace-pre-line">{factorInterp.recommendations}</p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Right: Facet table — UNCHANGED */}
                                        <div className="col-span-8">
                                            <table className="w-full text-xs border border-gray-200 rounded">
                                                <thead className="bg-gray-50">
                                                    <tr className="text-gray-600">
                                                        <th className="px-2 py-1.5 text-left font-medium border-b">Facet</th>
                                                        <th className="px-2 py-1.5 text-center font-medium border-b w-16">Stanine</th>
                                                        <th className="px-2 py-1.5 text-left font-medium border-b w-64">1  2  3  4  5  6  7  8  9  10</th>
                                                        <th className="px-2 py-1.5 text-left font-medium border-b">Interpretation</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {factor.facets.map((facet) => {
                                                        const facetInterp = facet.interpretation;
                                                        const facetText =
                                                            facetInterp?.interpretationText ||
                                                            facetInterp?.interpretation_text ||
                                                            '—';
                                                        const shortText = facetText.split(/(?<=\.)\s/)[0] || facetText;

                                                        return (
                                                            <tr key={facet.facetId}>
                                                                <td className="px-2 py-1.5 text-gray-800">{facet.label}</td>
                                                                <td className="px-2 py-1.5 text-center font-bold">{facet.stanine}</td>
                                                                <td className="px-2 py-1.5">{renderStanineBar(facet.stanine, 'small')}</td>
                                                                <td className="px-2 py-1.5 text-gray-600">{shortText}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
        );
    };

    // ============================================
    // FOOTER
    // ============================================
    const renderFooter = () => (
        <div className="mt-8 pt-4 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
            <div>
                <div className="font-bold text-[#063b56]">PsychCatalyst</div>
                <div className="italic">Psychometric Platform Assessment</div>
            </div>
        </div>
    );

    // ============================================
    // DOWNLOAD PDF
    // ============================================
    const handleDownloadPDF = async () => {
        if (!reportRef.current) {
            showError('Report not ready yet');
            return;
        }

        try {
            showInfo('Generating PDF...');

            const html2canvas = (await import('html2canvas')).default;
            const { jsPDF } = await import('jspdf');

            const photoImg = reportRef.current.querySelector('img[data-photo-candidate="true"]');
            let originalPhotoSrc = null;

            if (photoImg) {
                originalPhotoSrc = photoImg.src;
                try {
                    const res = await fetch(originalPhotoSrc, {
                        method: 'GET',
                        mode: 'cors',
                        credentials: 'omit',
                    });

                    if (res.ok) {
                        const blob = await res.blob();
                        const dataUrl = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.onerror = reject;
                            reader.readAsDataURL(blob);
                        });

                        photoImg.src = dataUrl;
                        photoImg.removeAttribute('crossOrigin');
                        await new Promise(r => requestAnimationFrame(r));
                    }
                } catch (err) {
                    console.warn('⚠️ Could not pre-fetch photo:', err.message);
                }
            }

            const actionsBar = document.querySelector('.no-print-download');
            if (actionsBar) actionsBar.style.display = 'none';

            await new Promise(r => setTimeout(r, 100));

            const canvas = await html2canvas(reportRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: false,
                backgroundColor: '#ffffff',
                logging: false,
                windowWidth: 1100,
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.98);

            const pdf = new jsPDF({
                unit: 'mm',
                format: 'a4',
                orientation: 'portrait',
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 6;
            const contentWidth = pageWidth - margin * 2;
            const contentHeight = (canvas.height * contentWidth) / canvas.width;

            let heightLeft = contentHeight;
            let position = margin;

            pdf.addImage(imgData, 'JPEG', margin, position, contentWidth, contentHeight);
            heightLeft -= (pageHeight - margin * 2);

            while (heightLeft > 0) {
                position = heightLeft - contentHeight + margin;
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', margin, position, contentWidth, contentHeight);
                heightLeft -= (pageHeight - margin * 2);
            }

            pdf.save(`Candidate_Report_${candidate?.full_name || candidate?.candidate_name || candidateId}.pdf`);

            if (photoImg && originalPhotoSrc) photoImg.src = originalPhotoSrc;
            if (actionsBar) actionsBar.style.display = '';

            showSuccess('✅ PDF downloaded!');

        } catch (err) {
            console.error('PDF generation error:', err);
            showError('Failed to generate PDF');
            const actionsBar = document.querySelector('.no-print-download');
            if (actionsBar) actionsBar.style.display = '';
        }
    };

    // ============================================
    // LOADING / ERROR
    // ============================================
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff9f2f] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading report...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#f4f6fb] py-8">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                        <XCircleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-700 mb-2">Error Loading Report</h3>
                        <p className="text-gray-500 mb-4">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a]"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ============================================
    // MAIN RENDER
    // ============================================
    return (
        <div className="min-h-screen bg-[#f4f6fb] py-8 print:bg-white print:py-0">
            <div className="max-w-6xl mx-auto px-4 print:px-0">
                <div className="mb-4 flex justify-between items-center print:hidden no-print-download">
                    <button
                        onClick={() => navigate('/admin')}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center text-sm"
                    >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back
                    </button>

                    <div className="flex gap-2">
                        <button
                            onClick={handleDownloadPDF}
                            className="px-4 py-2 bg-[#063b56] text-white rounded-lg hover:bg-[#0a4a6a] flex items-center text-sm"
                        >
                            <DownloadIcon className="h-4 w-4 mr-2" />
                            Download PDF
                        </button>
                    </div>
                </div>

                {candidate?.status === 'completed' ? (
                    <div ref={reportRef} className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 print:shadow-none print:border-0 print:p-0">
                        {renderHeader()}
                        {renderCandidateInfo()}
                        {renderResultsSummary()}
                        {renderDetailedInterpretation()}
                        {renderFooter()}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                        <XCircleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-700 mb-2">Assessment Not Completed</h3>
                        <p className="text-gray-500">This candidate has not completed the assessment yet.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CandidateReport;